import React, { FC } from 'react'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { get } from 'lodash'
import { useDispatch } from 'react-redux'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import {
  BankAccountStatus,
  ExternalWalletStatus,
  TransactionStatusEnum,
  TransactionTypeEnum,
  OrderType,
  HistoryEntity,
  isFiat,
} from '@sparling/financial-common'
import { Loader } from '../../Loader'
import { IColumns, IDataItem } from './TableTypes'
import { icons } from '../../../constants/icons'
import { GreenButton } from '../Buttons/GreenButton'
import {
  openAwaitingDepositPopup,
  openConfirmReceivedAmountPopup,
  cancelTransactionRequest,
} from '../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    margin: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& thead tr': {
      height: '60px',
      // padding: 2,
    },
    '& th': {
      color: theme.palette.primary.light,
      opacity: '0.6',
      fontWeight: 400,
      borderColor: 'rgba(233,236,239,0.38)',
    },
    '& tr': {
      height: '58px',
      '&:nth-child(even)': {
        backgroundColor: 'rgba(216,216,216,0.05)',
      },
    },
    '& td': {
      color: theme.palette.primary.light,
      fontSize: 16,
      border: 'none',
      '& .grey': {
        color: '#A5A5A5',
      },
      '& .green': {
        color: '#51E5A5',
      },
      '& .red': {
        color: '#ff6f5b',
      },
      '& .orange': {
        color: 'orange',
      },
    },
  },
  pagination: {
    marginTop: '24px',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    '& .MuiPaginationItem-root': {
      color: '#A9AAAD',
      fontSize: '0.75rem',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    '& .MuiPaginationItem-page.Mui-selected': {
      color: '#fff !important',
      textAlign: 'center',
      backgroundColor: 'rgba(104, 91, 198, 0.8)',
      border: '1px solid rgb(104, 91, 198)',
      borderRadius: '4px',
    },
    '& .MuiPagination-ul': {
      flexWrap: 'unset',
    },
  },
  container: {
    maxHeight: '1000px',
  },
  editIconGreen: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.secondary.light,
    },
  },
  editIconGray: {
    color: '#a8a9ac',
    paddingLeft: 0,
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  infoIcon: {
    color: '#a8a9ac',
    paddingLeft: 0,
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  errorIcon: {
    color: '#a8a9ac',
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  deleteIcon: {
    color: '#A9544B',
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
  tableTh: {
    textTransform: 'uppercase',
  },
  notFound: {
    backgroundColor: 'transparent !important',
    '& td': {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
  },
}))

interface TableProps {
  columns: IColumns[]
  data: IDataItem[]
  loadData?: (value: number) => void
  page?: string | number
  getUserId?: (value: string | number) => void
  onShow?: (id: number) => void
  onEdit?: (id: number) => void
  onDelete?: (id: number) => void
  twoAction?: true
  threeAction?: true
  isShowInfoIcon?: boolean
  loading?: boolean
  id?: string
  params: {
    currentPage: number
    itemsPerPage: number
    itemCount: number
    totalItems: number
    totalPages: number
  }
  tableContainerId?: string
}

export const TableComponent: FC<TableProps> = ({
  columns,
  data,
  loadData,
  getUserId,
  onShow,
  onEdit,
  onDelete,
  params,
  loading,
  id,
  // eslint-disable-next-line
  isShowInfoIcon = true,
  tableContainerId,
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  // eslint-disable-next-line
  const paginate = (event: any, pageNumber: any) => {
    // eslint-disable-next-line no-unused-expressions
    loadData && loadData(pageNumber)
  }

  const setColor = (column: string, row: IDataItem) => {
    let color = ''

    switch (column) {
      case 'status':
        switch (row.status) {
          // case TransactionStatusEnum.ACTION_NEEDED:
          case TransactionStatusEnum.REJECTED:
          case TransactionStatusEnum.INITIATED:
            // case TransactionStatusEnum.NEW:
            // case TransactionStatusEnum.UNDER_REVIEW:
            // case TransactionStatusEnum.APPROVED:
            color = 'orange'
            break
          // case TransactionStatusEnum.SIGNED:
          //   if (row.type === TransactionTypeEnum.DEPOSIT) color = 'orange'
          //   else if (row.type === TransactionTypeEnum.WITHDRAWAL) color = 'green'
          //   break
          case TransactionStatusEnum.COMPLETED:
            color = 'green'
            break
          default:
            color = '#FFFFFF'
        }
        break
      case 'type':
        switch (row.type) {
          case OrderType.BUY:
            color = 'green'
            break
          case OrderType.SELL:
            color = 'red'
            break
          default:
            color = '#FFFFFF'
        }
        break
      default:
        color = '#FFFFFF'
    }
    return color
  }

  return (
    <>
      {loading && <Loader />}

      <Paper className={classes.root}>
        <TableContainer {...(tableContainerId ? { id: tableContainerId } : {})}>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={`${column.id}-${Math.random()}`} align="left" className={classes.tableTh}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data && data.length > 0 ? (
                <>
                  {/* eslint-disable-next-line */}
                  {data.map((row: any) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        key={`${row.id}-${Math.random()}`}
                        onClick={() => (getUserId ? getUserId(row.id) : null)}
                      >
                        {columns.map((column, index) => (
                          <TableCell key={`${index}-${Math.random()}`}>
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {id === 'internal-wallets' && column.id === 'status' ? (
                              <>
                                <GreenButton
                                  onClick={() => {
                                    switch (row?.status) {
                                      case ExternalWalletStatus.AWAITING_DEPOSIT:
                                        dispatch(
                                          openAwaitingDepositPopup({
                                            address: row.address,
                                            created: get(row, 'metadata.whitelisting.createdAt'),
                                          }),
                                        )
                                        break
                                      case ExternalWalletStatus.AWAITING_CONFIRMATION:
                                        dispatch(openConfirmReceivedAmountPopup({ open: true, walletId: row.id }))
                                        break
                                      default:
                                        break
                                    }
                                  }}
                                  disabled={
                                    ![
                                      ExternalWalletStatus.AWAITING_DEPOSIT,
                                      ExternalWalletStatus.AWAITING_CONFIRMATION,
                                    ].includes(row[column.id])
                                  }
                                  text={row[column.id]
                                    ?.replace('Awaiting Confirmation', 'Verifying')
                                    ?.replace('Awaiting Deposit', 'Pending')}
                                  width={72}
                                />
                              </>
                            ) : // eslint-disable-next-line no-nested-ternary
                            ['transactionHistoryActions', 'unifiedHistoryActions'].includes(column.id) &&
                              // eslint-disable-next-line no-underscore-dangle
                              row?.__type === (HistoryEntity.TRANSACTION as const) &&
                              row?.type === TransactionTypeEnum.WITHDRAWAL &&
                              row?.status === TransactionStatusEnum.INITIATED &&
                              isFiat(row?.asset) ? (
                              <GreenButton
                                onClick={() => {
                                  dispatch(cancelTransactionRequest(row.id))
                                }}
                                text={'Cancel'}
                                width={72}
                              />
                            ) : // eslint-disable-next-line no-nested-ternary
                            id === 'client-bank-accounts' && column.id === 'status' ? (
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Typography>{row[column.id]}</Typography>
                                {row?.statusReason?.trim() && (
                                  <Tooltip title={`Reason: ${row?.statusReason}`} arrow>
                                    <InfoOutlinedIcon
                                      fontSize="small"
                                      sx={{
                                        color: 'grey',
                                        cursor: 'help',
                                        position: 'absolute',
                                        top: -10,
                                        right: -20,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            ) : !['actions', 'transactionHistoryActions', 'unifiedHistoryActions'].includes(
                                column.id,
                              ) ? (
                              <Box className={setColor(column.id, row)}>
                                {column.format
                                  ? column.format(row[column.id], row?.owner, row?.type, row?.riskScore, row)
                                  : row[column.id]}
                              </Box>
                            ) : (
                              <Box>
                                {onShow && (
                                  <IconButton className={classes.infoIcon} onClick={() => onShow(row.id)}>
                                    <span className="material-icons">{icons.VIEW}</span>
                                  </IconButton>
                                )}

                                {onEdit &&
                                  ((id === 'client-bank-accounts' && row.status === BankAccountStatus.PENDING) ||
                                    id !== 'client-bank-accounts') && (
                                    <IconButton className={classes.editIconGreen} onClick={() => onEdit(row.id)}>
                                      <span className="material-icons">{icons.EDIT}</span>
                                    </IconButton>
                                  )}

                                {onDelete && (
                                  <IconButton className={classes.deleteIcon} onClick={() => onDelete(row.id)}>
                                    <span className="material-icons">{icons.DELETE}</span>
                                  </IconButton>
                                )}
                                {!onShow && !onEdit && !onDelete && 'N/A'}
                              </Box>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </>
              ) : (
                <TableRow className={classes.notFound}>
                  <TableCell colSpan={columns.length} align={'center'}>
                    {!loading && 'No results found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {params && params.totalPages > 1 && (
          <Pagination
            className={classes.pagination}
            count={params.totalPages}
            defaultPage={params.currentPage}
            shape="rounded"
            onChange={paginate}
          />
        )}
      </Paper>
    </>
  )
}
